/**
 * This file is responsible for rendering Forge UI Kit components.
 * See https://developer.atlassian.com/platform/forge/ui-kit/ for more details.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useApolloClient } from '@apollo/react-hooks';

import {
  makeAvatar,
  makeAvatarStack,
  makeThreeLOPrompt,
  RendererNext,
  useWebRuntime,
} from '@atlassian/forge-ui';

import { ForgeUIExtensionProps } from '../../types';
import { useMemoedExtensionData } from '../../utils/appData';

export const ForgeUIKitRenderer = ({
  app,
  contextIds,
  coreData,
  entryPoint,
  extensionData,
  components,
  onInitialRender,
  getContextToken,
}: ForgeUIExtensionProps): JSX.Element => {
  const [isInitialRender, setInitialRender] = useState(true);

  const apolloClient = useApolloClient();

  const memoedExtensionData = useMemoedExtensionData(extensionData);
  const [dispatch, { forgeDoc, error, loading }] = useWebRuntime({
    apolloClient,
    contextIds,
    extensionId: app.id,
    coreData,
    entryPoint,
    getContextToken,
  });

  useEffect(() => {
    if (onInitialRender && isInitialRender && forgeDoc && !loading) {
      onInitialRender();
      setInitialRender(false);
    }
  }, [loading, forgeDoc, onInitialRender, isInitialRender, setInitialRender]);

  // Initialize effect
  useEffect(() => {
    dispatch({ type: 'render', extensionData: memoedExtensionData });
  }, [dispatch, memoedExtensionData]);

  // Event effect
  const dispatchWithData = useCallback(
    effect => {
      return dispatch({
        ...effect,
        extensionData: {
          ...effect.extensionData,
          ...memoedExtensionData,
        },
      });
    },
    [dispatch, memoedExtensionData]
  );

  const wiredComponents = useMemo(
    () => ({
      Avatar: makeAvatar({ client: apolloClient }),
      AvatarStack: makeAvatarStack({ client: apolloClient }),
      // `UserPicker` is not supported in Forge UI Kit. See https://ecosystem-platform.atlassian.net/browse/EXT-1557
    }),
    [apolloClient]
  );

  const getComponents = useCallback(
    defaults => ({
      ...defaults,
      ...wiredComponents,
      ...(components || []),
      ThreeLOPrompt: makeThreeLOPrompt({ appName: app.properties.title }),
    }),
    [wiredComponents, components, app.properties.title]
  );

  return (
    <RendererNext
      forgeDoc={forgeDoc}
      loading={loading}
      error={error}
      dispatch={dispatchWithData}
      components={getComponents}
      extension={app}
    />
  );
};
